import React, { useMemo, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { PositionedAction } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Typography, useMediaQuery, useTheme, Stack } from '@mui/material'
import {
  AI360SettingsControlTile,
  ConfirmSettingChangeDialog,
  GridSettingsControlTile,
  GridSettingSubPage,
  PVSettingsControlTile,
  PVSettingSubPage,
  SGReadySettingsControlTile,
} from '../components/settings-page'
import { InformationTile } from '../components/common'
import { makeStyles } from 'tss-react/mui'
import { DynamicEmsSettingsModel } from 'src/Libs/dynamic-power-prices/types'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useSettingsFormInitializer, useUpdatePowerSellPrices } from '../hooks'
import { Ai360SettingsFormValues } from '../types'
import { FeedInPowerModel } from 'src/Libs/installation-settings/model'
import { InstallationPowerPriceDto } from 'src/Libs/flow/installation/api'
import { useUpdateDynamicEmsSettings } from 'src/Libs/dynamic-power-prices'
import { useParams } from 'react-router-dom'
import { Extension, ExtensionCategoryNames } from 'src/Libs/installation-extensions'
import { TariffsModeModel } from '../model'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { updateDynamicEmsActivationState } from '../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { WEEKDAYS_POWER_SELL_PRICE } from '../consts'

interface Props {
  isOpen: boolean
  onClose: () => void
  dynamicEmsSettings: DynamicEmsSettingsModel | null
  feedInPowerData: FeedInPowerModel | undefined
  powerSellPrice: InstallationPowerPriceDto | null
  tariffsMode: TariffsModeModel | null
  extensions: Extension[]
  refetch: () => void
}

const useStyles = makeStyles()(theme => ({
  title: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
    },
  },
  informationTileContainer: {
    backgroundColor: theme.palette.warning.light,
    borderColor: theme.palette.warning.main,
  },
}))

const convertCentsToEuros = (cents: number): string => {
  return (cents / 100).toFixed(2)
}

// Defines the time of the day, when the given price is valid. Must always be set to 00:00 in that case.
const POWER_SELL_PRICE_FROM = '00:00'

const SettingsPage: React.FC<Props> = ({
  isOpen,
  onClose,
  dynamicEmsSettings,
  feedInPowerData,
  powerSellPrice,
  tariffsMode,
  extensions,
  refetch,
}) => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const [isPVSettingsOpen, setIsPVSettingsOpen] = useState<boolean>(false)
  const [isGridSettingsOpen, setIsGridSettingsOpen] = useState<boolean>(false)
  const [isConfirmDeactivateDialogOpen, setIsConfirmDeactivateDialogOpen] = useState(false)
  const { initialValues } = useSettingsFormInitializer(dynamicEmsSettings, feedInPowerData, powerSellPrice)
  const { mutateAsync: updateDynamicEmsSettings } = useUpdateDynamicEmsSettings()
  const { mutateAsync: updatePowerSellPrices } = useUpdatePowerSellPrices()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()

  const methods = useForm<Ai360SettingsFormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
  })

  const isAI360Active = useWatch({ control: methods.control, name: 'isAI360Active' })
  const isChargingFromPVActive = useWatch({ control: methods.control, name: 'isChargingFromPVActive' })
  const isChargingFromGridActive = useWatch({ control: methods.control, name: 'isChargingFromGridActive' })
  const pvSettings = useWatch({ control: methods.control, name: 'pvSettings' })

  const hasToDisplayAI360ActivationInfo = useMemo(() => {
    if (!isAI360Active) {
      return false
    }

    // Case 1: AI360 is active, but neither charging from PV nor from grid is active
    if (!isChargingFromPVActive && !isChargingFromGridActive) {
      return true
    }

    // Case 2: charging from PV is active, but details are not filled out
    if (isChargingFromPVActive && pvSettings.feedInTariffType === '') {
      return true
    }

    // Case 3: charging from grid is active, but there is no electricity tariff
    if (isChargingFromGridActive && tariffsMode?.mode === 'STATIC') {
      return true
    }

    return false
  }, [isAI360Active, isChargingFromGridActive, isChargingFromPVActive, pvSettings.feedInTariffType, tariffsMode?.mode])

  const hasToHighlightPvSettings = isAI360Active && isChargingFromPVActive && (pvSettings.feedInTariffType === '' || pvSettings.feedInTariff === '')
  const hasToHighlightGridSettings = isAI360Active && isChargingFromGridActive && tariffsMode?.mode === 'STATIC'

  const hasToDisableSaveButton = useMemo(() => {
    if (!methods.formState.isDirty) {
      return true
    }

    // Case 1: at least one optimization is active, but details are not filled out
    if (hasToHighlightPvSettings || hasToHighlightGridSettings) {
      return true
    }

    // Case 2: AI360 is active, but neither charging from PV nor from grid is active
    if (isAI360Active && !isChargingFromPVActive && !isChargingFromGridActive) {
      return true
    }

    return false
  }, [
    hasToHighlightGridSettings,
    hasToHighlightPvSettings,
    isAI360Active,
    isChargingFromGridActive,
    isChargingFromPVActive,
    methods.formState.isDirty,
  ])

  const activeExtension = useMemo(() => {
    return extensions.find(
      extension => extension.category.name === ExtensionCategoryNames.DynamicEnergyTariff && extension.contractState === 'active'
    )
  }, [extensions])

  const handleOnConfirmDeactivate = async (): Promise<void> => {
    try {
      /* Step 1: deactivate Dynamic Ems. This is required before updating the settings in the next step. */
      await updateDynamicEmsActivationState(storageId, { enableDynamicEms: false }, authFetch)

      /* Step 2: update Dynamic Ems settings */
      await updateDynamicEmsSettings({
        installationId: installationId!,
        feedInTariffType: null,
        isDynamicEmsEnabledForBattery: false,
        isSgReadyEnabled: false,
      })

      enqueueSuccessStack(t('ai360.settings-page.success-notification'))

      setIsConfirmDeactivateDialogOpen(false)
      refetch()
      onClose()
    } catch {
      enqueueErrorStack(t('ai360.settings-page.error-notification'))
    }
  }

  const onSubmit = async (data: Ai360SettingsFormValues): Promise<void> => {
    if (!data.isAI360Active) {
      setIsConfirmDeactivateDialogOpen(true)
      return
    }

    try {
      /* Activate Dynamic Ems. This is required before updating the settings in the next step. */
      await updateDynamicEmsActivationState(storageId, { enableDynamicEms: true }, authFetch)

      await updateDynamicEmsSettings({
        installationId: installationId!,
        isDynamicEmsEnabledForBattery: tariffsMode?.mode === 'STATIC' ? false : data.isChargingFromGridActive,
        isSgReadyEnabled: data.isSGReadyActive,
        feedInTariffType: data.isChargingFromPVActive ? (data.pvSettings.feedInTariffType === '' ? null : data.pvSettings.feedInTariffType) : null,
      })

      /** Update power sell prices if 'Charging from PV' was configured */
      if (data.pvSettings.feedInTariffType !== '') {
        await updatePowerSellPrices({
          installationId: installationId!,
          powerSellPrice: [
            {
              price: convertCentsToEuros(+data.pvSettings.feedInTariff),
              weekday: WEEKDAYS_POWER_SELL_PRICE,
              from: POWER_SELL_PRICE_FROM,
            },
          ],
        })
      }

      enqueueSuccessStack(t('ai360.settings-page.success-notification'))
      refetch()
      onClose()
    } catch {
      enqueueErrorStack(t('ai360.settings-page.error-notification'))
    }
  }

  const secondaryAction: PositionedAction = {
    position: '3',
    style: 'secondary',
    name: t('common.cancel'),
    onClick: onClose,
  }

  const primaryAction: PositionedAction = {
    position: '4',
    style: 'primary',
    name: t('common.save'),
    onClick: methods.handleSubmit(onSubmit),
    disabled: hasToDisableSaveButton,
  }

  const openPVSettings = (): void => {
    setIsPVSettingsOpen(true)
  }

  const openGridSettings = (): void => {
    setIsGridSettingsOpen(true)
  }

  return (
    <Dialog
      open={isOpen}
      fullScreen={!upMd}
      navigateBackButton={true}
      closeButtonVisible={false}
      onClose={onClose}
      onNavigateBack={onClose}
      title={t('ai360.settings-page.title')}
      dialogTitleClassName={classes.title}
      actions={[primaryAction, secondaryAction]}
      maxWidth="sm"
    >
      <FormProvider {...methods}>
        <Stack mt={2} gap={2}>
          <AI360SettingsControlTile />
          {hasToDisplayAI360ActivationInfo && (
            <InformationTile
              containerClassName={classes.informationTileContainer}
              iconColor={theme.palette.warning.main}
              message={t('ai360.settings-page.activate-ai360.info')}
            />
          )}
        </Stack>

        <Stack gap={2} mt={4}>
          <Typography variant="overline">{t('ai360.settings-page.headline.optimization')}</Typography>
          <PVSettingsControlTile containerClassName={cx({ [classes.informationTileContainer]: hasToHighlightPvSettings })} onClick={openPVSettings} />
          <GridSettingsControlTile
            containerClassName={cx({ [classes.informationTileContainer]: hasToHighlightGridSettings })}
            onClick={openGridSettings}
            tariffsMode={tariffsMode}
            activeExtension={activeExtension}
          />
        </Stack>

        <Stack gap={2} mt={4}>
          <Typography variant="overline">{t('ai360.settings-page.headline.external-consumer')}</Typography>
          <SGReadySettingsControlTile tariffsMode={tariffsMode} />
          {isAI360Active && !isChargingFromGridActive && <InformationTile message={t('ai360.settings-page.sg-ready-info')} />}
        </Stack>
        {isPVSettingsOpen && <PVSettingSubPage isOpen={isPVSettingsOpen} onClose={() => setIsPVSettingsOpen(false)} />}
        {isGridSettingsOpen && (
          <GridSettingSubPage
            isOpen={isGridSettingsOpen}
            onClose={() => setIsGridSettingsOpen(false)}
            tariffsMode={tariffsMode}
            activeExtension={activeExtension}
          />
        )}
        {isConfirmDeactivateDialogOpen && (
          <ConfirmSettingChangeDialog
            isOpen={isConfirmDeactivateDialogOpen}
            onClose={() => setIsConfirmDeactivateDialogOpen(false)}
            onConfirm={handleOnConfirmDeactivate}
            primaryActionText={t('common.deactivate')}
            title={t('ai360.settings-page.deactivate-ai360-dialog.title')}
            description={t('ai360.settings-page.deactivate-ai360-dialog.text')}
            infoMessage={t('ai360.settings-page.deactivate-ai360-dialog.info')}
          />
        )}
      </FormProvider>
    </Dialog>
  )
}

export default SettingsPage
